import { useTrackingContext, formAction, Tracker } from '@jbhi-fi/components/src/Tracking';
import { JBButton } from '@jbhi-fi/lanyard-ui';
import PerksBirthdayMascotIllustrated from '@jbhi-fi/lanyard-ui/components/Icons/PerksBirthdayMascotIllustrated';
import React, { useState } from 'react';
import { BIRTH_MONTH_DESCRIPTION, DEFAULT_BIRTHDAY_MESSAGE, MONTH_ARRAY } from '../../../customers/constants';
import { styles } from './AddBirthday.css';
import Select from '@jbhi-fi/lanyard-ui/components/Select';

const MONTH_SELECT_OPTIONS = MONTH_ARRAY.map((month, i) => ({
  label: month,
  value: `${i + 1}`,
}));

type AddBirthdayProps = {
  heading?: string;
  shouldShowDescription?: boolean;
  onSubmitBirthday: (birthMonth: number) => Promise<void>;
  onSkipSetBirthday: () => Promise<boolean> | Promise<void> | void;
};

export const AddBirthday = ({
  heading,
  shouldShowDescription = false,
  onSubmitBirthday,
  onSkipSetBirthday,
}: AddBirthdayProps) => {
  const [birthdayMonth, setBirthdayMonth] = useState<number | null>(null);
  const [isBirthdaySaving, setIsBirthdaySaving] = useState<boolean>(false);
  const [showBirthdayError, setShowBirthdayError] = useState<boolean>(false);

  const { tracker } = useTrackingContext();
  const handleTrackClickCta = (label: string) => {
    Tracker.trackCustomEvent({
      event: 'click_cta',
      link_text: label,
      location: 'header',
    });
  };

  const handleSubmit = async (
    event:
      | React.MouseEvent<HTMLButtonElement>
      | React.FormEvent<HTMLFormElement>
      | React.KeyboardEvent<HTMLInputElement>
  ) => {
    event.preventDefault();
    setIsBirthdaySaving(true);

    tracker.trackFormEvent({ label: 'Birth Month', action: formAction.fieldComplete });

    birthdayMonth && (await onSubmitBirthday(birthdayMonth));

    setIsBirthdaySaving(false);
    handleTrackClickCta('Save');
  };

  const handleSkipSetBirthday = async (event: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    event.preventDefault();
    setIsBirthdaySaving(true);
    tracker.trackClick({ label: 'No thanks' });
    handleTrackClickCta('Skip adding birthday');
    await onSkipSetBirthday();
    setIsBirthdaySaving(false);
  };

  const onBirthdayChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setShowBirthdayError(false);
    setBirthdayMonth(parseInt(event.target.value));
  };

  return (
    <div className={styles.container}>
      <div className={styles.iconContainer}>
        <PerksBirthdayMascotIllustrated />
      </div>

      <h3 className={styles.heading}>{heading || DEFAULT_BIRTHDAY_MESSAGE}</h3>

      <div>
        <Select
          id="birthday_month"
          label="Birthday month"
          name="birthday_month"
          onChange={e => onBirthdayChange(e)}
          errorMessage="Please select a birthday month."
          hasError={showBirthdayError}
          options={MONTH_SELECT_OPTIONS}
          value={`${birthdayMonth ?? ''}`}
          className={styles.birthdayInput}
        />

        {shouldShowDescription ? <small className={styles.fieldDescription}> {BIRTH_MONTH_DESCRIPTION}</small> : null}
      </div>

      <div className={styles.ctaContainer}>
        <JBButton
          type="primary"
          fullWidth
          loading={isBirthdaySaving}
          label={isBirthdaySaving ? 'Submitting...' : 'Save'}
          onClick={e => !isBirthdaySaving && handleSubmit(e)}
        />
        <JBButton
          classes={styles.skipButton}
          type="link"
          fullWidth
          label="No thanks"
          disabled={isBirthdaySaving}
          onClick={handleSkipSetBirthday}
        />
      </div>
    </div>
  );
};
